import { inject, Injectable } from '@angular/core';
import { getQuoteSummaryFees, getQuoteSummaryRestProducts, isUnaccompaniedMinorsProduct, QuoteService } from '@lx/quote-tool/shared/data-access/quote';
import { PackageProduct, ProductType, QuoteInputModel, QuoteOutput, QuoteOutputApi, QuoteProduct } from '@lx/quote-tool/shared/types';
import { LoggerService, parseApiError } from '@lx/shared/logger';
import { LoadingService } from '@lx/shared/ui/loading-spinner';
import { combineLatest, iif, Observable, of, retry, tap } from 'rxjs';
import { finalize, map, switchMap } from 'rxjs/operators';
import { QuoteOutputCourse, QuoteOutputProduct, QuoteOutputTransfer, QuoteState, QuoteToolLiteState, QuoteToolLiteStore } from '../store';
import { ExtrasSummary } from '../types';
import { ExtrasService } from './extras.service';
import { NavigationService } from './navigation.service';
import { QuoteInputMapper } from './quote-input.mapper';
import { OperationKey } from '../constants';

@Injectable({
  providedIn: 'root',
})
export class QuotePricesService {
  private readonly quoteService = inject(QuoteService);
  private readonly extrasService = inject(ExtrasService);
  private readonly quoteToolLiteStore = inject(QuoteToolLiteStore);
  private readonly quoteInputMapper = inject(QuoteInputMapper);
  private readonly navigationService = inject(NavigationService);
  private readonly loggerService = inject(LoggerService);
  private readonly loadingService = inject(LoadingService);

  getQuotePrices(): Observable<QuoteState> {
    // TODO improve this method to distinguish between the different flows
    return this.quoteToolLiteStore.selectionForQuotePrices$.pipe(
      tap(() => this.loadingService.startLoading($localize`Calculating prices...`, OperationKey.prices)),
      switchMap((selection) => {
        // If accommodation has a type different to OTHER, but it has not got a code it means
        // that the extras was reset. On that case, take the code from extras-summary endpoint
        // to fill the value with preselected value according to the selected type
        const { activeFlow, selectedSchoolLocation, selectedStartDate, selectedProductPackage } = selection || {};
        return iif(
          () => selection?.selectedAccommodation?.type !== 'OTHER' && !selection?.selectedAccommodation?.code,
          combineLatest([
            of(selection),
            this.extrasService.findExtrasSummary(activeFlow, selectedSchoolLocation?.schoolCode, selectedStartDate, selectedProductPackage),
          ]),
          combineLatest([of(selection), of(undefined)]),
        );
      }),
      switchMap(([selection, extrasSummary]: [Partial<QuoteToolLiteState>, ExtrasSummary]) =>
        of(this.quoteInputMapper.mapQuoteInput(selection, extrasSummary)),
      ),
      switchMap((quoteInput: QuoteInputModel) => this.quoteService.getQuotePriceV2(quoteInput)),
      retry({
        // Retry in case there is an API error
        count: 2,
        delay: (error, retryCount) => {
          this.loggerService.error(`Error getting quote prices. Retrying... Attempt ${retryCount}`, parseApiError(error));
          if (retryCount === 1) {
            // For the first retry, reset the selected extras
            this.quoteToolLiteStore.resetSelectedExtras();
          } else {
            // If the error still remains, navigate to first step (the course step) to initiate the iteration with the user again
            this.navigationService.navigateToFirstStep();
          }

          return this.quoteToolLiteStore.selectionForQuotePrices$;
        },
        resetOnSuccess: true,
      }),
      map((quoteOutput: QuoteOutputApi) => this.mapQuoteOutputToQuoteState(quoteOutput)),
      tap((quoteOutput: QuoteState) => {
        // Update the store with the accommodation code to make sure that accommodation always has a code
        // no matter if we have to use the accommodation from the extras summary or the selected accommodation
        this.quoteToolLiteStore.updateAccommodationCode(quoteOutput?.accommodation?.code);
      }),
      tap(() => {
        this.loadingService.stopLoading(OperationKey.prices);
      }),
      finalize(() => {
        this.loadingService.stopLoading(OperationKey.prices);
      }),
    );
  }

  private mapQuoteOutputToQuoteState(quoteOutput: QuoteOutput): QuoteState {
    if (!quoteOutput) {
      return undefined;
    }
    const {
      displayCurrency,
      destinationCurrency,
      singleCurrencyMode,
      grandTotal,
      productTotal,
      discountTotal,
      discountTotalAsPercentage,
      discounts,
      payableData,
      products,
      quoteId,
    } = quoteOutput;
    return {
      displayCurrency,
      destinationCurrency,
      singleCurrencyMode,
      grandTotal,
      productTotal,
      discountTotal,
      discountTotalAsPercentage,
      discounts,
      payableData,
      course: this.getQuoteProduct(products, 'TUITION'),
      accommodation: this.getQuoteProduct(products, 'ACCOMMODATION'),
      transfers: this.getTransfersFromQuoteProduct(products),
      feesAndSupplements: this.getFeesAndSupplementsFromQuoteProduct(products),
      professionalImmersionExperience: this.getQuoteProduct(products, 'PIE'),
      insurance: this.getQuoteProduct(products, 'INSURANCE'),
      premiumResidence: this.getQuoteProduct(products, 'PREMIUM_RESIDENCE'),
      privateLessons: this.getQuoteProduct(products, 'PRIVATE_LESSONS'),
      unaccompaniedMinorServices: products.filter((product) => isUnaccompaniedMinorsProduct(product)),
      premiumActivities: this.getPremiumActivitiesFromQuoteProduct(products),
      quoteId,
    };
  }

  private getTransfersFromQuoteProduct(products: QuoteProduct[]): QuoteOutputTransfer[] {
    const transfers = products.filter((product) => product.classification === 'TRANSFER');
    if (!transfers?.length) {
      return [];
    }

    return transfers.map(({ code, name, price, endPrice, discount, discountTotalAsPercentage, units, row, transferServiceType }) => ({
      code,
      name,
      price,
      endPrice,
      discount,
      discountTotalAsPercentage,
      units,
      row,
      transferServiceType,
    }));
  }

  private getFeesAndSupplementsFromQuoteProduct(products: QuoteProduct[]) {
    return [...getQuoteSummaryFees(products), ...getQuoteSummaryRestProducts(products)]?.map(
      ({ code, name, price, endPrice, discount, discountTotalAsPercentage, units }) => ({
        code,
        name,
        price,
        endPrice,
        discount,
        discountTotalAsPercentage,
        units,
      }),
    );
  }

  private getQuoteProduct(products: QuoteProduct[], classification: ProductType): QuoteOutputCourse | QuoteOutputProduct | undefined {
    const mainProduct = products.find((product) => product.classification === classification);
    if (mainProduct) {
      return this.mapMainProduct(mainProduct);
    }

    const includedProduct = products.flatMap((product) => product.includedProducts ?? []).find((product) => product.classification === classification);

    return includedProduct ? this.mapIncludedProduct(includedProduct) : undefined;
  }

  private mapMainProduct(product: QuoteProduct) {
    const { code, name, price, endPrice, discount, discountTotalAsPercentage, units, startDate, endDate } = product;
    return {
      code,
      name,
      price,
      endPrice,
      discount,
      discountTotalAsPercentage,
      units,
      startDate,
      endDate,
    };
  }

  private mapIncludedProduct({ code, name }: PackageProduct): QuoteOutputProduct {
    return {
      code,
      name,
    };
  }

  private getPremiumActivitiesFromQuoteProduct(products: QuoteProduct[]) {
    const premiumActivities = products.filter(({ classification }) => classification === 'PREMIUM_ACTIVITIES');
    if (!premiumActivities?.length) {
      return undefined;
    }

    return premiumActivities.map(({ code, name, price, endPrice, discount, discountTotalAsPercentage, units }) => ({
      code,
      name,
      price,
      endPrice,
      discount,
      discountTotalAsPercentage,
      units,
    }));
  }
}
